import React, {useContext} from "react";
import {useFormik} from "formik";
import {isEmpty} from "lodash";
import {FieldError, TextField} from "../../../common/fields";
import {LoadingContext} from "../../LoadingProvider";
import {useCustomerAccount} from "../../AccountProvider";
import { censorEmail, formatPhoneNumber2 } from "../../../../utils";
import {updateEmailWithOTP} from "../../../../api/custAccountApi";
import {getErrorCode, handleApiError} from "../../../../utils/errors";
import analytics from "../../../../utils/analytics";
import {ApiContext} from "../../../../contexts/ApiContext";
import {
  Button,
  ButtonGroup,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Typography
} from "@one-thd/sui-atomic-components";

const emailEditOtpFieldAutomationId = "login-information-email-form--edit-email-otp-field";
const emailEditOtpSubmitButtonAutomationId = "login-information-email-form--edit-email-otp-submit-button";
const emailEditOtpCancelButtonAutomationId = "login-information-email-form--edit-email-otp-cancel-button";

const VerifyOtpModal = ({isOpen, emailEditContext, onVerifyOtpSuccess, onVerifyOtpFailure, onCancelModal, onEvoiceRequest, evoiceErrors, currentEmail, updateAuthID}) => {

  const { email, phone, backendHostName, authId, userId, otpChannel } = emailEditContext;

  const { showLoading, hideLoading } = useContext(LoadingContext);
  const {autoToken} = useContext(ApiContext);
  const { account }  = useCustomerAccount();

  let verbiage;
  if (otpChannel === "sms") {
    verbiage = `Please enter the verification code we texted to ${formatPhoneNumber2(phone)}`
  } else if (otpChannel === "phone") {
    verbiage = ""
  } else {
    verbiage = `Please enter the verification code we emailed to ${censorEmail(currentEmail)}`;
  }

  const verifyOtpForm = useFormik({
    initialValues: {
      otp: "",
    },
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    validate: (values => {
      const errors = {};
      errors.otp = [];
      if(values.otp.length === 0) {
        errors.otp.push("Please enter your six digit code.");
      } else if(values.otp.length <= 5 && values.otp.length >= 1) {
        errors.otp.push("The verification code must be 6 digits");
      }
      return !isEmpty(errors.otp) && errors;
    }),

    onSubmit: (values, actions) => {

      showLoading();
      const {otp} = values;
      const cleanupForm = (callback) => {
        actions.resetForm();
        if(callback)
          callback(emailEditContext);
      }
      updateEmailWithOTP(otp, email, {backendHostName, authId, userId}, account, autoToken).then(({response, error}) => {

        if(response) {
          cleanupForm(onVerifyOtpSuccess);
          analytics.postSettingsSubmissionEvent("security:email address edit success");
        } else {
          const errorCode = getErrorCode(error);
          const errorMessage = handleApiError(error, "change email");
          if(errorCode === "AUTH_ERR_125") {
            cleanupForm(onVerifyOtpFailure);
          } else {
            actions.setFieldError("otp", [errorMessage])
          }

          if((errorCode === "PASS_ERR_015") && (!!error?.response?.headers?.authid)){
            updateAuthID(error?.response?.headers?.authid);
          }
        }
        hideLoading();
      });

    },
  });

  const cleanupForm = (callback) => {
    verifyOtpForm.resetForm();
    if(callback)
      callback(emailEditContext);
  }

  const onModalClose = () => cleanupForm(onCancelModal);

  const onOtpChange = (e) => {
    const otp = e.target.value.trim().replace(/[^\d]/,"");
    verifyOtpForm.setFieldValue("otp", otp, true);
  }

  const VerifyOtpTextBox = () => <>
    <TextField maxLength={6}
      autoFocus // eslint-disable-line
      id="verifyOTP_EmailEdit"
      name="otp"
      dataAutomationId={emailEditOtpFieldAutomationId}
      type="OTP" label="Verification Code"
      valid={!(verifyOtpForm.touched.otp && !isEmpty(verifyOtpForm.errors.otp))}
      onChange={onOtpChange}
      onBlur={verifyOtpForm.handleBlur}
      value={verifyOtpForm.values.otp}>
      {verifyOtpForm.touched.otp && !isEmpty(verifyOtpForm.errors.otp) &&
      <FieldError name="otp" errors={verifyOtpForm.errors.otp}/>}
    </TextField>
  </>;

  function VerifySmsOtpFormBody() {
    return (
      <>
        <button type="button" className="u__default-link" onClick={onEvoiceRequest}>
          Call Me With the Code Instead
        </button>
        {!isEmpty(evoiceErrors) && (
            <div className="form-input--error">
              <FieldError errors={evoiceErrors}/>
            </div>
        )}
      </>
    )
  }

  return (
      <Modal open={isOpen} onClose={onModalClose}>
        <ModalHeader>
          Verify Your Identity
        </ModalHeader>
        <ModalBody>
          <div className='sui-grid sui-gap-4'>
            {(verbiage) && (
                <Typography variant='h5'>
                  {verbiage}
                </Typography>
            )}
            <VerifyOtpTextBox/>
            {otpChannel === "sms" && (<VerifySmsOtpFormBody />)}
            {otpChannel === "phone" && (
                <div className="alert-inline alert-inline--success inline">
                  We&apos;re calling you with a verification code at {formatPhoneNumber2(phone)}. Please enter the
                  verification code above.
                </div>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
        <ButtonGroup fullWidth orientation="vertical">
            <Button variant='primary' onClick={verifyOtpForm.handleSubmit} disabled={!verifyOtpForm.isValid} data-automation-id={emailEditOtpSubmitButtonAutomationId}>
              Verify
            </Button>
            <Button onClick={onModalClose} data-automation-id={emailEditOtpCancelButtonAutomationId}>
              Cancel
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </Modal>
  )
}


export default VerifyOtpModal;